import { useEffect, useMemo, useRef, useState } from 'react'
import Modal from 'react-modal'
import {
  CreateSimulatedPersonRequestDto,
  DATE_OF_BIRTH_FORMAT,
  GENDER_LABELS,
  GENDER_SUB_GENDERS,
  Gender,
  SimulatedPersonDto,
  SubGender,
  UpdateSimulatedPersonRequestDto,
  isValidAge,
  isValidDate,
} from '@ivy-monorepo/shared'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'
import TextInput from '../ui/TextInput'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

interface SimulatedPersonModalProps extends BaseModalProps {
  person: SimulatedPersonDto | null
  onSaved: (person: SimulatedPersonDto) => void
  onDeleted?: (publicId: string) => void
}

const PRIMARY_GENDERS = [Gender.Female, Gender.Male, Gender.NonBinary, Gender.Trans, Gender.Unknown]

const DEFAULT_PREFERRED = [Gender.Female, Gender.Male]

const toggleGender = (values: Gender[], gender: Gender): Gender[] => {
  if (values.includes(gender)) {
    return values.filter(item => item !== gender)
  }
  return [...values, gender]
}

const toFormDob = (stored: string): string => {
  const parsed = dayjs.utc(stored, DATE_OF_BIRTH_FORMAT, true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const toApiDob = (formValue: string): string | null => {
  const parsed = dayjs(formValue, 'YYYY-MM-DD', true)
  if (!parsed.isValid()) {
    return null
  }
  return parsed.format(DATE_OF_BIRTH_FORMAT)
}

const SimulatedPersonModal = ({ isOpen, onClose, person, onSaved, onDeleted }: SimulatedPersonModalProps) => {
  const isEdit = person !== null
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [gender, setGender] = useState<Gender>(Gender.Female)
  const [subGender, setSubGender] = useState<SubGender | ''>('')
  const [preferredGenders, setPreferredGenders] = useState<Gender[]>(DEFAULT_PREFERRED)
  const [showGenderPreferences, setShowGenderPreferences] = useState(true)
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [vibeEmoji, setVibeEmoji] = useState('')
  const [notes, setNotes] = useState('')
  const [isPhotoVerified, setIsPhotoVerified] = useState(true)
  const [isActive, setIsActive] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  const subGenderOptions = useMemo(() => GENDER_SUB_GENDERS[gender] ?? [], [gender])

  useEffect(() => {
    if (!isOpen) return
    setFirstName(person?.firstName ?? '')
    setLastName(person?.lastName ?? '')
    setImageUrl(person?.imageUrl ?? '')
    setGender(person?.gender ?? Gender.Female)
    setSubGender(person?.subGender ?? '')
    setPreferredGenders(person?.preferredGenders?.length ? [...person.preferredGenders] : DEFAULT_PREFERRED)
    setShowGenderPreferences(person?.showGenderPreferences ?? true)
    setDateOfBirth(person?.dateOfBirth ? toFormDob(person.dateOfBirth) : '')
    setVibeEmoji(person?.vibeEmoji ?? '')
    setNotes(person?.notes ?? '')
    setIsPhotoVerified(person?.isPhotoVerified ?? true)
    setIsActive(person?.isActive ?? true)
    setError(null)
    setIsSaving(false)
    setIsDeleting(false)
    setIsUploading(false)
  }, [isOpen, person])

  useEffect(() => {
    if (subGender && !subGenderOptions.includes(subGender)) {
      setSubGender('')
    }
  }, [subGender, subGenderOptions])

  const handleClose = () => {
    if (isSaving || isDeleting || isUploading) return
    onClose()
  }

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    preferredGenders.length > 0 &&
    dateOfBirth.trim().length > 0

  const handleSubmit = async () => {
    const trimmedFirstName = firstName.trim()
    const trimmedLastName = lastName.trim()
    const storedDob = toApiDob(dateOfBirth.trim())

    if (!trimmedFirstName || !trimmedLastName) {
      setError('First name and last name are required.')
      return
    }
    if (preferredGenders.length === 0) {
      setError('Select at least one preferred gender.')
      return
    }
    if (!storedDob || !isValidDate(storedDob) || !isValidAge(storedDob)) {
      setError('Enter a valid date of birth for ages 18–100.')
      return
    }

    const payload: CreateSimulatedPersonRequestDto | UpdateSimulatedPersonRequestDto = {
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      imageUrl: imageUrl.trim() || null,
      gender,
      subGender: subGender || null,
      preferredGenders,
      showGenderPreferences,
      dateOfBirth: storedDob,
      vibeEmoji: vibeEmoji.trim() || null,
      notes: notes.trim() || null,
      isPhotoVerified,
      isActive,
    }

    setIsSaving(true)
    setError(null)
    try {
      const saved = isEdit
        ? await adminService.updateSimulatedPerson(person.publicId, payload)
        : await adminService.createSimulatedPerson(payload as CreateSimulatedPersonRequestDto)
      onSaved(saved)
      onClose()
    } catch {
      setError(isEdit ? 'Failed to save simulated person.' : 'Failed to create simulated person.')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePhotoPicked = async (file: File | undefined) => {
    if (!file) return
    setIsUploading(true)
    setError(null)
    try {
      setImageUrl(await adminService.uploadSimulatedPersonImage(file))
    } catch {
      setError('Failed to upload photo.')
    } finally {
      setIsUploading(false)
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const handleDelete = async () => {
    if (!isEdit || !person || !onDeleted) return
    if (!window.confirm(`Delete simulated person ${person.firstName} ${person.lastName}?`)) return

    setIsDeleting(true)
    setError(null)
    try {
      await adminService.deleteSimulatedPerson(person.publicId)
      onDeleted(person.publicId)
      onClose()
    } catch {
      setError('Failed to delete simulated person.')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup role-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>{isEdit ? 'Edit simulated person' : 'Add simulated person'}</h2>
        <p style={{ marginTop: 0, opacity: 0.85, fontSize: '0.95rem' }}>
          Catalog personas for investor demos. They are not accounts and cannot log in.
        </p>
        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <TextInput required type="text" label="First name" name="simFirstName" value={firstName} onChange={setFirstName} />
          <TextInput required type="text" label="Last name" name="simLastName" value={lastName} onChange={setLastName} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            {imageUrl.trim() ? (
              <img
                src={imageUrl.trim()}
                alt=""
                style={{ width: 72, height: 72, borderRadius: 8, objectFit: 'cover', background: '#eee' }}
              />
            ) : (
              <div style={{ width: 72, height: 72, borderRadius: 8, background: '#eee' }} />
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={e => handlePhotoPicked(e.target.files?.[0])}
            />
            <button
              type="button"
              className="styled-button"
              onClick={() => photoInputRef.current?.click()}
              disabled={isUploading || isSaving}
            >
              {isUploading ? 'Uploading…' : imageUrl.trim() ? 'Change photo' : 'Upload photo'}
            </button>
            {imageUrl.trim() && (
              <button type="button" className="styled-button" onClick={() => setImageUrl('')} disabled={isUploading || isSaving}>
                Remove
              </button>
            )}
          </div>
          <TextInput
            type="text"
            label="Image URL (or paste a link)"
            name="simImageUrl"
            required={false}
            value={imageUrl}
            onChange={setImageUrl}
          />
          <TextInput
            required
            type="date"
            label="Date of birth"
            name="simDateOfBirth"
            value={dateOfBirth}
            onChange={setDateOfBirth}
          />
          <TextInput
            type="text"
            label="Vibe emoji"
            name="simVibeEmoji"
            required={false}
            value={vibeEmoji}
            onChange={setVibeEmoji}
          />
          <TextInput
            type="textArea"
            label="Notes"
            name="simNotes"
            required={false}
            value={notes}
            onChange={setNotes}
          />

          <div className="input-container valid type-text">
            <label className="filled" htmlFor="simGender">
              Gender
            </label>
            <select
              id="simGender"
              name="simGender"
              value={gender}
              onChange={e => setGender(e.target.value as Gender)}
              required
            >
              {PRIMARY_GENDERS.map(value => (
                <option key={value} value={value}>
                  {GENDER_LABELS[value] ?? value}
                </option>
              ))}
            </select>
          </div>

          {subGenderOptions.length > 0 && (
            <div className="input-container valid type-text">
              <label className="filled" htmlFor="simSubGender">
                Sub-gender
              </label>
              <select
                id="simSubGender"
                name="simSubGender"
                value={subGender}
                onChange={e => setSubGender((e.target.value || '') as SubGender | '')}
              >
                <option value="">None</option>
                {subGenderOptions.map(value => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Preferred genders</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.25rem' }}>
              {PRIMARY_GENDERS.filter(value => value !== Gender.Unknown).map(value => (
                <label key={value} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input
                    type="checkbox"
                    checked={preferredGenders.includes(value)}
                    onChange={() => setPreferredGenders(prev => toggleGender(prev, value))}
                  />
                  <span>{GENDER_LABELS[value] ?? value}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem 1.5rem', marginBottom: '0.5rem' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <input
                type="checkbox"
                checked={showGenderPreferences}
                onChange={e => setShowGenderPreferences(e.target.checked)}
              />
              <span>Show gender preferences</span>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <input type="checkbox" checked={isPhotoVerified} onChange={e => setIsPhotoVerified(e.target.checked)} />
              <span>Photo verified</span>
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
              <span>Active</span>
            </label>
          </div>
        </form>
        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}
        <div className="modal-footer-content">
          {isEdit && onDeleted && (
            <button
              type="button"
              className="styled-button"
              onClick={handleDelete}
              disabled={isSaving || isDeleting}
              style={{ marginRight: 'auto' }}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving || isDeleting}>
            Cancel
          </button>
          <button
            type="button"
            className="styled-button"
            onClick={handleSubmit}
            disabled={isSaving || isDeleting || isUploading || !canSubmit}
          >
            {isSaving ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save changes' : 'Add person'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default SimulatedPersonModal
